import { useEffect, useRef, useState } from "react";
import { Mic, Square, Check, X } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { getAllProductsForVoice, getProductUnits, getActiveVoiceTraining } from "../../services/productService";
import { matchProductAndQuantity, resolveUnitAndFinalize } from "../../utils/voiceOrderParser";

// Phase 40 — Smart Voice Order Input.
//
// Voice is an INPUT METHOD ONLY: recognize -> parse -> match real product ->
// match real unit -> extract quantity -> show confirmation -> user confirms
// -> existing addItem(). It never adds to the cart on its own, never sets
// price (the cart's own price fields come from the real product/unit data,
// exactly like a manual Add to Cart click), and never bypasses the active
// product/unit filtering ProductCard.jsx already enforces (Phase 38).
//
// Architecture note: frontend_Admin's own Voice Order calls a backend AI
// parser, POST /api/voice/parse — but that whole router is staff_only
// (backend/routers/voice.py), unreachable for a guest/customer without an
// RBAC change this phase forbids. See voiceOrderParser.js's own header for
// the full reasoning. This component is a self-contained equivalent that
// matches locally against the real product list already fetched from the
// existing public GET /products / GET /products/{id}/units endpoints.

const STATE = { IDLE: "idle", LISTENING: "listening", PROCESSING: "processing", CONFIRM: "confirm", ERROR: "error" };

function getSpeechRecognitionCtor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export default function VoiceOrderButton() {
  const { addItem } = useCart();

  const [supported] = useState(() => Boolean(getSpeechRecognitionCtor()));
  const [lang, setLang] = useState("km"); // "km" | "en" — this shop's real product names are Khmer, so Khmer is the more useful default
  const [state, setState] = useState(STATE.IDLE);
  const [message, setMessage] = useState("");
  // The in-progress voice result the confirmation panel edits before the
  // user commits it — never written to the cart until "Add to Cart".
  const [pending, setPending] = useState(null); // { rawText, product, activeUnits, unit, qty, candidates? }

  const recRef = useRef(null);
  const productsRef = useRef(null); // lazy-loaded full active catalog, fetched once
  const trainingRef = useRef(null); // Phase 42 — lazy-loaded active training feed, fetched once alongside products
  const resultHandledRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Component unmount / navigating away from Shop — recognition must
      // stop, never keep listening in the background (Section 26).
      mountedRef.current = false;
      try { recRef.current?.stop(); } catch { /* already stopped */ }
      recRef.current = null;
    };
  }, []);

  function flash(msg, ms = 3500) {
    setMessage(msg);
    if (ms) setTimeout(() => { if (mountedRef.current) setMessage((cur) => (cur === msg ? "" : cur)); }, ms);
  }

  async function loadProductsOnce() {
    if (productsRef.current) return productsRef.current;
    const list = await getAllProductsForVoice();
    productsRef.current = list;
    return list;
  }

  // Phase 42 — fetched once per component lifetime (not per spoken word),
  // same caching discipline as loadProductsOnce. A failure here is
  // non-fatal: voice ordering still works on product names alone, exactly
  // as it did before Global Voice Training existed — training only ever
  // ADDS recognition ability, never something the core flow depends on.
  async function loadTrainingOnce() {
    if (trainingRef.current) return trainingRef.current;
    let map = new Map();
    try {
      const rows = await getActiveVoiceTraining();
      for (const row of rows) {
        if (row.intent !== "ADD_PRODUCT" || !row.product_id || !row.spoken_phrase) continue;
        if (!map.has(row.product_id)) map.set(row.product_id, []);
        map.get(row.product_id).push(row.spoken_phrase);
      }
    } catch {
      map = new Map(); // no trained phrases available — fall back silently
    }
    trainingRef.current = map;
    return map;
  }

  async function handleFinalTranscript(transcript) {
    const text = (transcript || "").trim();
    setState(STATE.PROCESSING);
    if (!text) {
      setState(STATE.ERROR);
      flash("⚠️ Not recognized. Please try again.");
      return;
    }

    let products;
    try {
      products = await loadProductsOnce();
    } catch {
      setState(STATE.ERROR);
      flash("Unable to load products right now. Please try again.");
      return;
    }
    const trainingByProductId = await loadTrainingOnce();

    const step1 = matchProductAndQuantity(text, products, trainingByProductId);
    if (step1.status === "no_product") {
      setState(STATE.ERROR);
      flash(`Product not found for: "${text}". Please try again.`);
      return;
    }
    if (step1.status === "ambiguous_product") {
      setState(STATE.CONFIRM);
      setPending({ kind: "ambiguous", rawText: step1.rawText, candidates: step1.candidates, qty: step1.qty, invalid: step1.invalid, textWithoutQty: step1.textWithoutQty });
      return;
    }
    await resolveProductChoice(step1);
  }

  // Shared by both the direct (unambiguous) path and after the user picks
  // one of several suggested products.
  async function resolveProductChoice({ product, rawText, textWithoutQty, qty, invalid }) {
    let activeUnits = [];
    try {
      const units = await getProductUnits(product.id);
      // Phase 38's rule, reused here verbatim: an inactive unit is never
      // selectable — voice must not be a backdoor around that (Section 24).
      activeUnits = units.filter((u) => u.status === "Active");
    } catch {
      setState(STATE.ERROR);
      flash("Unable to load this product's units right now. Please try again.");
      return;
    }

    const result = resolveUnitAndFinalize({ rawText, textWithoutQty, qty, invalid, product, activeUnits });
    setState(STATE.CONFIRM);
    if (result.status === "ready") {
      setPending({ kind: "ready", rawText, product, activeUnits, unit: result.unit, qty: result.qty });
    } else if (result.status === "no_unit") {
      // The product has units but none matched the spoken words — offer the
      // product's own real units to choose from (Section 19), don't just
      // reject outright.
      setPending({ kind: "pick_unit", rawText, product, activeUnits, unit: null, qty: invalid ? null : qty });
    } else if (result.status === "no_quantity") {
      setPending({ kind: "need_qty", rawText, product, activeUnits, unit: result.unit, qty: null });
    } else if (result.status === "invalid_quantity") {
      setPending({ kind: "need_qty", rawText, product, activeUnits, unit: result.unit, qty: null, invalidQtyMessage: true });
    }
  }

  function pickAmbiguousProduct(product) {
    if (!pending) return;
    resolveProductChoice({ product, rawText: pending.rawText, textWithoutQty: pending.textWithoutQty, qty: pending.qty, invalid: pending.invalid });
  }

  function pickUnit(unit) {
    setPending((p) => (p ? { ...p, kind: p.qty ? "ready" : "need_qty", unit } : p));
  }

  function setQty(value) {
    const n = Number(value);
    setPending((p) => {
      if (!p) return p;
      const valid = Number.isFinite(n) && n > 0 && Number.isInteger(n);
      return { ...p, qty: valid ? n : null, kind: valid && (p.unit || p.activeUnits.length === 0) ? "ready" : p.kind === "ready" ? "need_qty" : p.kind };
    });
  }

  function confirmAdd() {
    if (!pending || pending.kind !== "ready") return;
    const inStock = (pending.product.stock ?? 0) > 0;
    if (!inStock) {
      flash(`"${pending.product.name}" is out of stock.`);
      return;
    }
    addItem(pending.product, pending.unit, pending.qty);
    flash(`✓ Added: ${pending.product.name} × ${pending.qty}`);
    setPending(null);
    setState(STATE.IDLE);
  }

  function cancelPending() {
    setPending(null);
    setState(STATE.IDLE);
  }

  function stopListening() {
    try { recRef.current?.stop(); } catch { /* already stopped */ }
  }

  function startListening() {
    const SR = getSpeechRecognitionCtor();
    if (!SR) {
      flash("Voice input is not supported in this browser. Please use a supported browser or type the item.", 5000);
      return;
    }
    setPending(null);
    setMessage("");

    const rec = new SR();
    rec.lang = lang === "km" ? "km-KH" : "en-US";
    // Single utterance per click — simpler and clearer for a one-shot
    // "speak one order line, confirm, add" flow than a continuously
    // re-arming recognizer; a fresh instance every click also means there
    // is never a stale/duplicate recognizer to leak across attempts
    // (Section 27/40).
    rec.continuous = false;
    rec.interimResults = false;
    recRef.current = rec;
    resultHandledRef.current = false;
    setState(STATE.LISTENING);

    rec.onresult = (e) => {
      // One tap, one utterance: the moment a FINAL result arrives, stop
      // recognition explicitly rather than relying on continuous=false to
      // end it implicitly — the user should never need to click Stop after
      // speaking (Phase 40.2). resultHandledRef is set first so a
      // same-tick/near-simultaneous onend never re-runs this path.
      resultHandledRef.current = true;
      try { rec.stop(); } catch { /* already stopping/stopped */ }
      const last = e.results[e.results.length - 1];
      const transcript = last && last[0] ? last[0].transcript : "";
      handleFinalTranscript(transcript);
    };

    rec.onerror = (e) => {
      resultHandledRef.current = true;
      const messages = {
        "not-allowed": "Microphone permission is required for voice input.",
        "permission-denied": "Microphone permission is required for voice input.",
        "no-speech": "⚠️ No speech detected. Please try again.",
        "audio-capture": "No microphone found.",
        network: "Network error during speech recognition — check your connection.",
      };
      setState(STATE.ERROR);
      flash(messages[e.error] || "⚠️ Voice input error. Please try again.");
    };

    rec.onend = () => {
      // Covers the case where recognition just stops with no result and no
      // error (e.g. silence/timeout) — the UI must never stay stuck on
      // "Listening…" (Section 7/28).
      if (!resultHandledRef.current && mountedRef.current) {
        setState(STATE.IDLE);
      }
    };

    try {
      rec.start();
    } catch {
      setState(STATE.ERROR);
      flash("Unable to start voice input. Please try again.");
    }
  }

  if (!supported) {
    return (
      <div className="text-xs text-neutral-400" title="Voice input is not supported in this browser. Please use a supported browser or type the item.">
        🎙️ Voice unavailable
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5">
        <div role="group" aria-label="Speech language (voice input only, not the page language)" className="flex overflow-hidden rounded-full border border-neutral-300 text-[11px] font-semibold">
          <button type="button" onClick={() => setLang("km")} aria-pressed={lang === "km"} title="Speak in Khmer"
            className={`px-2 py-1 transition ${lang === "km" ? "bg-neutral-900 text-white" : "bg-white text-neutral-500 hover:bg-neutral-50"}`}>
            KM
          </button>
          <button type="button" onClick={() => setLang("en")} aria-pressed={lang === "en"} title="Speak in English"
            className={`border-l border-neutral-300 px-2 py-1 transition ${lang === "en" ? "bg-neutral-900 text-white" : "bg-white text-neutral-500 hover:bg-neutral-50"}`}>
            EN
          </button>
        </div>
        <button
          type="button"
          onClick={state === STATE.LISTENING ? stopListening : startListening}
          disabled={state === STATE.PROCESSING}
          title="Voice order"
          aria-label={state === STATE.LISTENING ? "Stop voice input" : "Start voice input"}
          className={`flex h-9 w-9 items-center justify-center rounded-full text-white shadow-sm transition disabled:opacity-60 ${
            state === STATE.LISTENING ? "animate-pulse bg-red-500" : "bg-neutral-900 hover:bg-neutral-800"
          }`}
        >
          {state === STATE.LISTENING ? <Square size={15} /> : <Mic size={16} />}
        </button>
      </div>

      {state === STATE.LISTENING && (
        <p className="absolute right-0 top-11 z-10 whitespace-nowrap rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 shadow-sm">
          🔴 Listening… ({lang === "km" ? "Khmer" : "English"})
        </p>
      )}
      {state === STATE.PROCESSING && (
        <p className="absolute right-0 top-11 z-10 whitespace-nowrap rounded-lg bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-600 shadow-sm">
          ⏳ Processing…
        </p>
      )}
      {message && (
        <p className="absolute right-0 top-11 z-10 w-64 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 shadow-sm">
          {message}
        </p>
      )}

      {pending && (
        <div className="absolute right-0 top-11 z-20 w-72 rounded-xl border border-neutral-200 bg-white p-3.5 shadow-lg">
          <p className="mb-2 text-xs font-semibold text-neutral-500">🎙️ Voice Result</p>

          {pending.kind === "ambiguous" ? (
            <>
              <p className="mb-2 text-xs text-neutral-600">Multiple products found. Please choose:</p>
              <div className="flex flex-col gap-1.5">
                {pending.candidates.map((c) => (
                  <button key={c.id} type="button" onClick={() => pickAmbiguousProduct(c)}
                    className="rounded-lg border border-neutral-200 px-2.5 py-1.5 text-left text-xs text-neutral-800 hover:bg-neutral-50">
                    {c.name}
                  </button>
                ))}
              </div>
              <button type="button" onClick={cancelPending} className="mt-2 text-xs text-neutral-400 underline">Cancel</button>
            </>
          ) : (
            <>
              <div className="space-y-1.5 text-xs">
                <div>
                  <span className="text-neutral-400">Product: </span>
                  <span className="font-medium text-neutral-900">{pending.product.name}</span>
                </div>

                <div>
                  <span className="text-neutral-400">Unit: </span>
                  {pending.activeUnits.length === 0 ? (
                    <span className="text-neutral-500">—</span>
                  ) : pending.kind === "pick_unit" ? (
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {pending.activeUnits.map((u) => (
                        <button key={u.unit_code} type="button" onClick={() => pickUnit(u)}
                          className="rounded-full border border-neutral-300 px-2.5 py-1 text-[11px] hover:border-neutral-500">
                          {u.unit_code}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="font-medium text-neutral-900">{pending.unit?.unit_code}</span>
                  )}
                </div>

                <div>
                  <span className="text-neutral-400">Quantity: </span>
                  {pending.kind === "need_qty" ? (
                    <input
                      type="number"
                      min="1"
                      step="1"
                      autoFocus
                      placeholder="Enter quantity"
                      onChange={(e) => setQty(e.target.value)}
                      className="mt-1 w-20 rounded border border-neutral-300 px-2 py-1 text-xs"
                    />
                  ) : (
                    <span className="font-medium text-neutral-900">{pending.qty}</span>
                  )}
                </div>

                {pending.invalidQtyMessage && (
                  <p className="text-[11px] text-red-600">Quantity must be greater than 0 — please enter it below.</p>
                )}
                {pending.kind === "need_qty" && !pending.invalidQtyMessage && (
                  <p className="text-[11px] text-amber-600">Please say (or enter) the quantity.</p>
                )}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={confirmAdd}
                  disabled={pending.kind !== "ready"}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Check size={13} /> Add to Cart
                </button>
                <button type="button" onClick={cancelPending}
                  className="flex items-center justify-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-xs text-neutral-600 hover:bg-neutral-50">
                  <X size={13} /> Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
